import React, { useState, useEffect } from 'react';
import {
  X,
  User,
  Shield,
  Plane,
  Car,
  HeartPulse,
  Briefcase,
  GraduationCap,
  Users,
  Home,
  Sparkles,
  ArrowRight,
} from 'lucide-react';

export const IDENTITY_TEMPLATES = [
  {
    id: 'personal',
    title: 'Personal Identity',
    identityType: 'personal',
    category: 'Personal',
    tags: ['personal', 'profile', 'primary'],
    icon: User,
    color: '#8b5cf6',
    description: 'Core personal credentials including full name, birth details, contact info, and home address.',
    defaultTitle: 'Personal: [Your Name]',
    fields: {
      fullName: 'Nikhil Sharma',
      dob: '1996-05-14',
      nationality: 'United States',
      gender: 'Male',
      email: 'nikhil@example.com',
      phone: '+1 (555) 234-5678',
      address: {
        street: '742 Evergreen Terrace',
        city: 'San Francisco',
        state: 'CA',
        postalCode: '94107',
        country: 'United States',
      },
      documentNumber: '',
      notes: 'Primary personal identity record stored with zero-knowledge encryption.',
    },
  },
  {
    id: 'government_id',
    title: 'Government ID / National ID',
    identityType: 'government_id',
    category: 'Personal',
    tags: ['national-id', 'ssn', 'government'],
    icon: Shield,
    color: '#3b82f6',
    description: 'National Identification Card, Social Security Number, Citizen ID, or state identity card.',
    defaultTitle: 'National ID: [Your Name]',
    fields: {
      fullName: 'Nikhil Sharma',
      documentNumber: 'SSN-984-21-4920',
      nationality: 'United States',
      issuingAuthority: 'Social Security Administration',
      issueDate: '2014-06-10',
      expiryDate: '',
      notes: 'Keep confidential. Required for tax filings and official background verifications.',
    },
  },
  {
    id: 'passport',
    title: 'Passport',
    identityType: 'passport',
    category: 'Personal',
    tags: ['passport', 'travel', 'border'],
    icon: Plane,
    color: '#06b6d4',
    description: 'International travel passport with passport number, place of birth, issuing country, and expiry reminders.',
    defaultTitle: 'Passport: [Your Name]',
    fields: {
      fullName: 'Nikhil Sharma',
      documentNumber: 'US-849201948',
      nationality: 'United States',
      issuingAuthority: 'U.S. Department of State',
      issueDate: '2020-04-12',
      expiryDate: '2030-04-11',
      notes: 'Includes diplomatic RFID biometric chip. Keep copy of scan attached.',
    },
  },
  {
    id: 'driving_licence',
    title: 'Driving Licence',
    identityType: 'driving_licence',
    category: 'Personal',
    tags: ['driver-license', 'dmv', 'vehicle'],
    icon: Car,
    color: '#10b981',
    description: 'Motor vehicle driving permit with license number, state authority, endorsements, and expiry date.',
    defaultTitle: 'Driver Licence: California',
    fields: {
      fullName: 'Nikhil Sharma',
      documentNumber: 'DL-F9481029',
      nationality: 'United States',
      issuingAuthority: 'California DMV',
      issueDate: '2022-08-15',
      expiryDate: '2027-08-14',
      notes: 'Class C Commercial & Passenger vehicle authorized. Real ID compliant.',
    },
  },
  {
    id: 'health_insurance',
    title: 'Health / Insurance ID',
    identityType: 'health_insurance',
    category: 'Personal',
    tags: ['insurance', 'medical', 'health'],
    icon: HeartPulse,
    color: '#ec4899',
    description: 'Medical health plan, dental, prescription drug coverage card, group ID, and emergency contact.',
    defaultTitle: 'Health Insurance: BlueCross',
    fields: {
      fullName: 'Nikhil Sharma',
      documentNumber: 'MEM-894019284',
      issuingAuthority: 'Anthem Blue Cross Blue Shield',
      issueDate: '2024-01-01',
      expiryDate: '2026-12-31',
      notes: 'Group No: GRP-774102\nRxBIN: 003858\nEmergency 24/7 Hotline: +1 (800) 555-0199',
    },
  },
  {
    id: 'work',
    title: 'Work Identity',
    identityType: 'work',
    category: 'Work',
    tags: ['work', 'employee-badge', 'corporate'],
    icon: Briefcase,
    color: '#6366f1',
    description: 'Corporate employee badge, staff ID number, department, job title, and enterprise credentials.',
    defaultTitle: 'Work ID: CipherVault HQ',
    fields: {
      fullName: 'Nikhil Sharma',
      documentNumber: 'EMP-4019',
      issuingAuthority: 'CipherVault Inc.',
      email: 'nikhil@ciphervault.com',
      issueDate: '2023-03-01',
      expiryDate: '2028-02-28',
      notes: 'Role: Principal Security Engineer\nOffice: HQ West, Building 4\nAccess Tier: Level 4 Super-Admin',
    },
  },
  {
    id: 'student',
    title: 'Student Identity',
    identityType: 'student',
    category: 'Personal',
    tags: ['student', 'university', 'campus'],
    icon: GraduationCap,
    color: '#f59e0b',
    description: 'Academic student card, roll number, university department, library access, and validity period.',
    defaultTitle: 'Student ID: Stanford University',
    fields: {
      fullName: 'Nikhil Sharma',
      documentNumber: 'STU-994182',
      issuingAuthority: 'Stanford University',
      issueDate: '2021-09-01',
      expiryDate: '2025-06-30',
      notes: 'Department of Computer Science\nGraduate Studies Division\nCampus Access: Gates Computer Science Hall',
    },
  },
  {
    id: 'family',
    title: 'Family Member',
    identityType: 'family',
    category: 'Family',
    tags: ['family', 'dependent', 'emergency'],
    icon: Users,
    color: '#f43f5e',
    description: 'Details for family members, spouses, children, or dependents for medical and emergency coordination.',
    defaultTitle: 'Family: [Dependent Name]',
    fields: {
      fullName: 'Sarah Sharma',
      dob: '1998-11-23',
      nationality: 'United States',
      gender: 'Female',
      phone: '+1 (555) 987-6543',
      documentNumber: '',
      notes: 'Relationship: Spouse\nBlood Group: O+\nEmergency Primary Contact: Yes',
    },
  },
  {
    id: 'address',
    title: 'Address Identity',
    identityType: 'address',
    category: 'Personal',
    tags: ['address', 'billing', 'residence'],
    icon: Home,
    color: '#14b8a6',
    description: 'Verified residential or corporate billing address profile with proof of residency document attachments.',
    defaultTitle: 'Residence Address: California',
    fields: {
      fullName: 'Nikhil Sharma',
      address: {
        street: '742 Evergreen Terrace, Suite 400',
        city: 'San Francisco',
        state: 'CA',
        postalCode: '94107',
        country: 'United States',
      },
      notes: 'Primary residential billing address for official bank statements and utilities.',
    },
  },
  {
    id: 'custom',
    title: 'Custom Identity',
    identityType: 'custom',
    category: 'Custom',
    tags: ['custom', 'id'],
    icon: Sparkles,
    color: '#eab308',
    description: 'Customizable scaffold for specialized registrations, membership cards, or club identification.',
    defaultTitle: 'Custom Identity: [Title]',
    fields: {
      fullName: 'Nikhil Sharma',
      documentNumber: '',
      issuingAuthority: '',
      notes: 'Enter custom notes, member credentials, or custom attributes here.',
    },
  },
];

import {
  Search,
  Check,
} from 'lucide-react';

export const TEMPLATE_CATEGORIES = [
  'All',
  'Personal',
  'Travel',
  'Work',
  'Student',
  'Family',
  'Custom',
];

export default function IdentityTemplatesSidebar({ open, onClose, onSelectTemplate }) {
  const [selectedId, setSelectedId] = useState(IDENTITY_TEMPLATES[0].id);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  // Hide bottom dock and prevent background scroll while drawer is open, restore on close/unmount
  useEffect(() => {
    if (open) {
      document.body.classList.add('mobile-modal-open', 'template-modal-open');
    } else {
      document.body.classList.remove('mobile-modal-open', 'template-modal-open');
    }
    return () => document.body.classList.remove('mobile-modal-open', 'template-modal-open');
  }, [open]);

  if (!open) return null;

  const filteredTemplates = IDENTITY_TEMPLATES.filter((tpl) => {
    const matchesCategory =
      activeCategory === 'All' ||
      tpl.category.toLowerCase() === activeCategory.toLowerCase() ||
      (activeCategory === 'Travel' && tpl.identityType === 'passport') ||
      (activeCategory === 'Government' && tpl.identityType === 'government_id');

    const q = searchQuery.trim().toLowerCase();
    if (!q) return matchesCategory;

    const matchesSearch =
      tpl.title.toLowerCase().includes(q) ||
      tpl.description.toLowerCase().includes(q) ||
      tpl.category.toLowerCase().includes(q) ||
      tpl.tags.some((t) => t.toLowerCase().includes(q));

    return matchesCategory && matchesSearch;
  });

  const activeTemplate =
    IDENTITY_TEMPLATES.find((t) => t.id === selectedId) ||
    filteredTemplates[0] ||
    IDENTITY_TEMPLATES[0];

  return (
    <div className="identity-templates-drawer-wrapper">
      {/* Backdrop overlay */}
      <div
        className="identity-templates-drawer-overlay animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Right Drawer Panel */}
      <aside
        className="identity-templates-drawer animate-slide-left"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Reusable Identity Templates"
      >
        {/* Drawer Header */}
        <div className="templates-drawer-header">
          <div className="templates-drawer-header-left">
            <div className="templates-drawer-icon-badge">
              <Sparkles size={20} />
            </div>
            <div className="templates-drawer-title-group">
              <h2 className="templates-drawer-title">Reusable Identity Templates</h2>
              <p className="templates-drawer-subtitle">
                Select from 10 specialized templates configured for government IDs, travel, or family credentials.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-icon modal-close-btn"
            onClick={onClose}
            aria-label="Close templates drawer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Search & Category Filter */}
        <div className="templates-drawer-toolbar">
          <div className="templates-drawer-search-box">
            <Search size={15} className="templates-search-icon" />
            <input
              type="text"
              className="templates-search-input"
              placeholder="Search identity templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="templates-search-clear"
                onClick={() => setSearchQuery('')}
              >
                &times;
              </button>
            )}
          </div>

          <div className="templates-drawer-categories">
            {TEMPLATE_CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`templates-cat-pill ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Scrollable Templates Body */}
        <div className="templates-drawer-body">
          {/* Templates Cards List */}
          <div className="templates-drawer-list">
            {filteredTemplates.length === 0 ? (
              <div className="templates-empty-state">
                <p>No identity templates match "{searchQuery}"</p>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => {
                    setSearchQuery('');
                    setActiveCategory('All');
                  }}
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              filteredTemplates.map((tpl) => {
                const Icon = tpl.icon;
                const isSelected = tpl.id === activeTemplate.id;
                return (
                  <div
                    key={tpl.id}
                    className={`identity-template-card ${isSelected ? 'active' : ''}`}
                    onClick={() => setSelectedId(tpl.id)}
                  >
                    <div className="identity-template-card-top">
                      <div
                        className="identity-template-icon-box"
                        style={{
                          background: `${tpl.color}16`,
                          color: tpl.color,
                          borderColor: `${tpl.color}35`,
                        }}
                      >
                        <Icon size={20} />
                      </div>
                      <div className="identity-template-info">
                        <div className="identity-template-title-row">
                          <h4 className="identity-template-title">{tpl.title}</h4>
                          <span
                            className="identity-template-category-badge"
                            style={{
                              background: `${tpl.color}15`,
                              color: tpl.color,
                              borderColor: `${tpl.color}30`,
                            }}
                          >
                            {tpl.category}
                          </span>
                        </div>
                        <p className="identity-template-desc">{tpl.description}</p>
                      </div>
                      <div className={`template-select-radio ${isSelected ? 'selected' : ''}`}>
                        {isSelected && <Check size={13} strokeWidth={3} />}
                      </div>
                    </div>

                    {/* Quick tags row */}
                    <div className="identity-template-tags-row">
                      {tpl.tags.map((tg) => (
                        <span key={tg} className="identity-template-tag-chip">
                          #{tg}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Active Template Detailed Preview */}
          {activeTemplate && (
            <div className="template-drawer-preview">
              <div className="template-preview-header">
                <div className="template-preview-badge-row">
                  <span
                    className="template-preview-badge"
                    style={{
                      background: `${activeTemplate.color}18`,
                      color: activeTemplate.color,
                    }}
                  >
                    {activeTemplate.category}
                  </span>
                  <span className="template-preview-type">Type: {activeTemplate.identityType}</span>
                </div>
                <h3 className="template-preview-title">{activeTemplate.defaultTitle}</h3>
              </div>

              <div className="template-preview-fields-box">
                <span className="template-preview-section-title">Pre-configured Fields</span>
                <div className="template-preview-fields-grid">
                  <div className="preview-field-cell">
                    <span className="preview-cell-label">Full Name</span>
                    <span className="preview-cell-value">{activeTemplate.fields.fullName || '—'}</span>
                  </div>
                  {activeTemplate.fields.documentNumber && (
                    <div className="preview-field-cell">
                      <span className="preview-cell-label">Document ID</span>
                      <span className="preview-cell-value font-mono">
                        {activeTemplate.fields.documentNumber}
                      </span>
                    </div>
                  )}
                  {activeTemplate.fields.nationality && (
                    <div className="preview-field-cell">
                      <span className="preview-cell-label">Nationality</span>
                      <span className="preview-cell-value">{activeTemplate.fields.nationality}</span>
                    </div>
                  )}
                  {activeTemplate.fields.issuingAuthority && (
                    <div className="preview-field-cell">
                      <span className="preview-cell-label">Issuing Authority</span>
                      <span className="preview-cell-value">{activeTemplate.fields.issuingAuthority}</span>
                    </div>
                  )}
                  {activeTemplate.fields.dob && (
                    <div className="preview-field-cell">
                      <span className="preview-cell-label">Date of Birth</span>
                      <span className="preview-cell-value">{activeTemplate.fields.dob}</span>
                    </div>
                  )}
                  {activeTemplate.fields.expiryDate && (
                    <div className="preview-field-cell">
                      <span className="preview-cell-label">Expiry Date</span>
                      <span className="preview-cell-value">{activeTemplate.fields.expiryDate}</span>
                    </div>
                  )}
                </div>
              </div>

              {activeTemplate.fields.notes && (
                <div className="template-preview-notes-box">
                  <span className="template-preview-section-title">Encrypted Notes Template</span>
                  <p className="template-preview-notes-text">{activeTemplate.fields.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sticky Drawer Footer */}
        <div className="templates-drawer-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              onSelectTemplate(activeTemplate);
              onClose();
            }}
          >
            <span>Use "{activeTemplate.title}"</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </aside>
    </div>
  );
}

// Named alias export for backwards compatibility
export { IdentityTemplatesSidebar as IdentityTemplatesModal };

